import type {
  ChatEvent,
  ClawerContact,
  CreditBalance,
  Draft,
  HealthStatus,
  Message,
  Move,
  Nudge,

  ReviewQueueItem,
  ReflectionDetail,
  ReflectionSummary,
  ScheduledMessage,
  SemanticSearchResult,
  SetupPayload,
  SetupStatus,
  Tag,
  UsageResponse,
  WizardState,
  TimelineEvent,
  TrustLevel,
} from '../types';
import type { TelemetryConfig } from '../types';

// Backend URL resolution order:
// 1. localStorage "clawer_api_url" — per-user backend (each user owns their VPS)
// 2. VITE_API_BASE_URL env var — build-time default for self-hosters
// 3. "" — relative URLs (monolith / reverse proxy)
export const API_BASE = localStorage.getItem('clawer_api_url')
  ?? (import.meta.env.VITE_API_BASE_URL as string | undefined)
  ?? '';

async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (!(options?.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const token = localStorage.getItem('clawer_auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...headers, ...options.headers as Record<string, string> },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return response.json();
}

// Contact API
export const contactsAPI = {
  list: (params?: { q?: string; tag?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.tag) qs.set('tag', params.tag);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return fetchAPI<ClawerContact[]>(`/api/contacts${query ? '?' + query : ''}`);
  },
  get: (id: number) => fetchAPI<ClawerContact>(`/api/contacts/${id}`),
  messages: (id: number, params?: { limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return fetchAPI<Message[]>(`/api/contacts/${id}/messages${query ? '?' + query : ''}`);
  },
  timeline: (id: number, params?: { limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return fetchAPI<TimelineEvent[]>(`/api/contacts/${id}/timeline${query ? '?' + query : ''}`);
  },
  addTag: (contactId: number, tagId: number) =>
    fetchAPI(`/api/contacts/${contactId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tag_id: tagId }),
    }),
  removeTag: (contactId: number, tagId: number) =>
    fetchAPI(`/api/contacts/${contactId}/tags/${tagId}`, { method: 'DELETE' }),
  reprocess: () =>
    fetchAPI<{ queued: number; message: string }>('/api/contacts/reprocess', { method: 'POST' }),
  historySync: () =>
    fetchAPI<{ chats: number; ingested: number; skipped: number }>('/api/contacts/history-sync', { method: 'POST' }),
  trackAll: () =>
    fetchAPI<{ updated: number; message: string }>('/api/contacts/track-all', { method: 'POST' }),
  updateLanguage: (contactId: number, languageOverride: string | null) =>
    fetchAPI(`/api/contacts/${contactId}/language`, {
      method: 'PATCH',
      body: JSON.stringify({ language_override: languageOverride }),
    }),
  updateTracking: (contactId: number, trackingOverride: boolean | null) =>
    fetchAPI<{ id: number; tracking_override: boolean | null; is_tracked: boolean }>(`/api/contacts/${contactId}/tracking`, {
      method: 'PATCH',
      body: JSON.stringify({ tracking_override: trackingOverride }),
    }),
  bulkTrack: (phones: string[]) =>
    fetchAPI<{ matched: number; not_found: string[]; updated: number }>('/api/contacts/bulk-track', {
      method: 'POST',
      body: JSON.stringify({ phones }),
    }),
  bulkUntrack: (phones: string[]) =>
    fetchAPI<{ matched: number; not_found: string[]; updated: number }>('/api/contacts/bulk-untrack', {
      method: 'POST',
      body: JSON.stringify({ phones }),
    }),
  resetAllTracking: () =>
    fetchAPI<{ updated: number }>('/api/contacts/reset-all-tracking', { method: 'POST' }),
};

// Tags API
export const tagsAPI = {
  list: () => fetchAPI<Tag[]>('/api/tags'),
  create: (data: { label: string; color?: string; icon?: string }) =>
    fetchAPI<Tag>('/api/tags', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id: number) => fetchAPI(`/api/tags/${id}`, { method: 'DELETE' }),
};

// Health API
export const healthAPI = {
  detailed: () => fetchAPI<HealthStatus>('/health/detailed'),
};

// Moves API
export const movesAPI = {
  list: (params?: { status?: string; contact_id?: number; due_date?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.contact_id) qs.set('contact_id', String(params.contact_id));
    if (params?.due_date) qs.set('due_date', params.due_date);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return fetchAPI<Move[]>(`/api/moves${query ? '?' + query : ''}`);
  },
  update: (id: number, data: { status: 'done' | 'skipped' | 'active' }) =>
    fetchAPI<Move>(`/api/moves/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

// Semantic search API
export const searchAPI = {
  list: (params: { q: string; contact_id?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    qs.set('q', params.q);
    if (params.contact_id) qs.set('contact_id', String(params.contact_id));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchAPI<SemanticSearchResult[]>(`/api/search?${qs.toString()}`);
  },
};

// Language / translation API
export const languageAPI = {
  translate: (body: { text: string; source_language: string; target_language: string }) =>
    fetchAPI<{ translated_text: string }>('/api/translate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  translateMessage: (messageId: number, targetLanguage: string) =>
    fetchAPI<{ translated_text: string }>(`/api/messages/${messageId}/translate`, {
      method: 'POST',
      body: JSON.stringify({ target_language: targetLanguage }),
    }),

  transcribe: (audio: Blob) => {
    const form = new FormData();
    form.append('audio', audio, 'recording.wav');
    return fetchAPI<{ text: string; detected_language: string | null }>('/api/transcribe', {
      method: 'POST',
      body: form,
    });
  },
};

async function* parseSSE(response: Response): AsyncGenerator<ChatEvent> {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() || '';

    for (const chunk of chunks) {
      const lines = chunk.split('\n');
      let event = 'token';
      let data = '{}';
      for (const line of lines) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        if (line.startsWith('data:')) data = line.slice(5).trim();
      }
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(data);
      } catch {
        parsed = {};
      }
      yield { event: event as ChatEvent['event'], data: parsed };
    }
  }
}

export const chatAPI = {
  async *stream(payload: { thread_id: string; message: string }): AsyncGenerator<ChatEvent> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    const token = localStorage.getItem('clawer_auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE}/api/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
      throw new Error(err.detail || `HTTP ${response.status}`);
    }
    for await (const event of parseSSE(response)) {
      yield event;
    }
  },
  threads: () => fetchAPI<{ threads: string[] }>('/api/chat/threads'),
  confirmAction: (actionId: string, decision: string = 'approved') =>
    fetchAPI(`/api/chat/actions/${actionId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    }),
};

export const nudgesAPI = {
  list: (params?: { status?: 'active' | 'snoozed' | 'dismissed' | 'all' }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return fetchAPI<Nudge[]>(`/api/nudges${query ? '?' + query : ''}`);
  },
  snooze: (id: string, duration: '3d' | '1w' | 'custom' = '3d', until?: string) =>
    fetchAPI<Nudge>(`/api/nudges/${id}/snooze`, {
      method: 'PATCH',
      body: JSON.stringify({ duration, until }),
    }),
  dismiss: (id: string) =>
    fetchAPI<Nudge>(`/api/nudges/${id}/dismiss`, { method: 'PATCH' }),
  unsnooze: (id: string) =>
    fetchAPI<Nudge>(`/api/nudges/${id}/unsnooze`, { method: 'PATCH' }),
  clearRecent: (contactId: number, messageAt: string) =>
    fetchAPI<{ cleared: number; ids: string[] }>('/api/nudges/clear-recent', {
      method: 'POST',
      body: JSON.stringify({ contact_id: contactId, message_at: messageAt }),
    }),
};

export const draftsAPI = {
  list: (contactId: number, includeArchived: boolean = false) =>
    fetchAPI<Draft[]>(`/api/drafts?contact_id=${contactId}&include_archived=${includeArchived}`),
  latest: (contactId: number) => fetchAPI<Draft>(`/api/drafts/latest/${contactId}`),
  regenerate: (contactId: number, nudgeId?: string) =>
    fetchAPI<Draft>(`/api/drafts/${contactId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ nudge_id: nudgeId || null }),
    }),
  update: (draftId: string, content: string) =>
    fetchAPI<Draft>(`/api/drafts/${draftId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    }),
  queue: (draftId: string) =>
    fetchAPI<{ status: string; note: string }>(`/api/drafts/${draftId}/queue`, {
      method: 'POST',
      body: JSON.stringify({ intent: 'queue' }),
    }),
};

export const outboundAPI = {
  sendNow: async (contactId: number, content: string) => {
    const res = await fetchAPI<{ success: boolean; message_id: string | null; error: string | null }>('/api/outbound/send', {
      method: 'POST',
      body: JSON.stringify({ contact_id: contactId, content }),
    });
    return res;
  },
  updateTrustLevel: (contactId: number, trustLevel: TrustLevel) =>
    fetchAPI<{ contact_id: number; trust_level: TrustLevel }>(`/api/contacts/${contactId}/trust-level`, {
      method: 'PATCH',
      body: JSON.stringify({ trust_level: trustLevel }),
    }),
  createScheduled: (payload: { contact_id: number; content: string; send_at: string; original_text?: string; was_translated?: boolean }) =>
    fetchAPI<ScheduledMessage>('/api/outbound/scheduled', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listScheduled: (params?: { status_filter?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status_filter) qs.set('status_filter', params.status_filter);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return fetchAPI<ScheduledMessage[]>(`/api/outbound/scheduled${query ? `?${query}` : ''}`);
  },
  cancelScheduled: (id: string) =>
    fetchAPI<{ id: string; status: string }>(`/api/outbound/scheduled/${id}/cancel`, {
      method: 'POST',
    }),
  listReview: (params?: { status_filter?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status_filter) qs.set('status_filter', params.status_filter);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return fetchAPI<ReviewQueueItem[]>(`/api/outbound/review${query ? `?${query}` : ''}`);
  },
  approveReview: (id: string) =>
    fetchAPI<{ id: string; status: string; reason: string; error: string | null }>(`/api/outbound/review/${id}/approve`, {
      method: 'POST',
    }),
  rejectReview: (id: string) =>
    fetchAPI<{ id: string; status: string; reason: string; error: string | null }>(`/api/outbound/review/${id}/reject`, {
      method: 'POST',
    }),
  overrideSend: (id: string) =>
    fetchAPI<{ id: string; status: string; reason: string; error: string | null }>(`/api/outbound/review/${id}/override-send`, {
      method: 'POST',
    }),
  resumeCircuitBreaker: () =>
    fetchAPI<{ status: string; breaker: Record<string, unknown> }>('/api/outbound/circuit-breaker/resume', {
      method: 'POST',
    }),
};

export const reflectionsAPI = {
  list: () => fetchAPI<ReflectionSummary[]>('/api/reflections'),
  detail: (week: string) => fetchAPI<ReflectionDetail>(`/api/reflections/${week}`),
};

export interface UpdateInfo {
  current: string;
  latest: string;
  update_available: boolean;
  release_notes: string;
}

export const updateAPI = {
  check: () => fetchAPI<UpdateInfo>('/api/update/check'),
  apply: (version: string) =>
    fetchAPI<{ status: string; image: string }>('/api/update/apply', {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),
};

// Credits API — calls studio service (owns billing data)
async function fetchStudio<T>(url: string, token: string): Promise<T> {
  const r = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<T>;
}

export const creditsAPI = {
  balance: (studioUrl: string, gatewayToken: string) =>
    fetchStudio<CreditBalance>(`${studioUrl}/api/credits/balance`, gatewayToken),

  usage: (studioUrl: string, gatewayToken: string) =>
    fetchStudio<UsageResponse>(`${studioUrl}/api/credits/usage`, gatewayToken),
};

// Telemetry API
export const telemetryAPI = {
  async getConfig(): Promise<TelemetryConfig> {
    return fetchAPI<TelemetryConfig>('/api/settings/telemetry');
  },
  async updateConfig(sharingTier: number): Promise<TelemetryConfig> {
    return fetchAPI<TelemetryConfig>('/api/settings/telemetry', {
      method: 'PUT',
      body: JSON.stringify({ sharing_tier: sharingTier }),
    });
  },
};

// Pipeline API
export const pipelineAPI = {
  run: (jobs: string[]) =>
    fetchAPI<{ run_group: string; jobs: Array<{ job_name: string; status: string }> }>(
      '/api/pipeline/run',
      { method: 'POST', body: JSON.stringify({ jobs }) }
    ),
  status: (runGroup: string) =>
    fetchAPI<{
      run_group: string;
      jobs: Array<{
        job_name: string;
        status: string;
        total_items: number;
        processed_items: number;
        error_message: string | null;
      }>;
    }>(`/api/pipeline/status/${runGroup}`),
};

// Setup wizard API
export const setupAPI = {
  status: () => fetchAPI<SetupStatus>('/api/setup/status'),
  loadState: () => fetchAPI<WizardState>('/api/setup/state'),
  saveState: (state: Partial<WizardState>) =>
    fetchAPI<{ ok: boolean }>('/api/setup/state', {
      method: 'PUT',
      body: JSON.stringify(state),
    }),
  validateKey: (body: { provider: string; api_key?: string; ollama_url?: string }) =>
    fetchAPI<{ valid: boolean; error?: string }>('/api/setup/validate-api-key', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  complete: (body: SetupPayload) =>
    fetchAPI<{ success: boolean; redirect?: string; error?: string; auth_token?: string; gateway_token?: string }>('/api/setup/complete', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  storeGatewayToken: (gatewayToken: string) =>
    fetchAPI<{ ok: boolean }>('/api/setup/gateway-token', {
      method: 'PUT',
      body: JSON.stringify({ gateway_token: gatewayToken }),
    }),
  restartQR: () =>
    fetchAPI<{ status: string }>('/api/setup/restart-qr', { method: 'POST' }),
};
